
import {
  computed,
  defineComponent,
  PropType,
  ref,
  shallowRef,
  toRef,
  watch,
  inject,
  onMounted,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { FormatMethods } from "@/helpers/data-format";
import { FormApi } from "@/services/api/Form.service";
import { SiteApi } from "@/services/api/Site.service";
import { IForm } from "@/interfaces/iForm";
import { IForms } from "@/interfaces/iSite";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { getLookups } from "@/services/api/Lookup.service";
import { hasPermission } from "@/core/services/permission.service";
import {
  FormSiteTableBulkDelete,
  FormSiteTableFilters,
  FormSiteTableHeaders,
} from "../dataTableOptions";
import { useToast } from "vue-toastification";
import * as Yup from "yup";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { SITE_TABS } from "@/views/site/siteDataTableOptions";
import { DrawerComponent } from "@/assets/ts/components";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import SiteDetailModal from "@/views/site/modal/SiteDetailModal.vue";
import { Emitter } from "mitt";
import { debounce } from "lodash";
interface ITabsEmit {
  tabs: ITabConfig[];
  title: string;
  icon?: string;
}
type Events = {
  VZTabsUpdate: ITabsEmit;
};
export default defineComponent({
  props: {
    data: { type: Object as PropType<IForm>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: {
    Datatable,
    VZDeleteModal,
    PreviewLink,
    SiteDetailModal,
  },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let options: { label: any; value: string }[] = [];
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showSearch = true;
    const tableData = ref<IForms[]>([]);
    const allTableData = ref<IForms[]>([]);
    const showDetailDialog = ref<any>(false);
    const showDeleteDialog = ref<any>(false);
    const selectedForAction = ref<Partial<IForm>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(FormSiteTableBulkDelete());
    const tableHeader = ref(FormSiteTableHeaders());
    const tableFilter = ref(FormSiteTableFilters());
    const toast = useToast();
    const formDeleteMsg = t("pages.site.formDeleteMsg");
    const formSiteAddMsg = t("pages.site.siteAssignedMsg");
    const Datatable = ref();
    const emitter: Emitter<Events> | undefined = inject("emitter");
    const defaultFilter = ref({ status: "ACTIVE" });
    const format = computed(() => {
      return FormatMethods;
    });
    let permissionModel = ref<any>({
      create: "VYZOR:TEMPLATES:FORMS:CREATE",
      delete: "VYZOR:TEMPLATES:FORMS:DELETE",
      update: "VYZOR:TEMPLATES:FORMS:UPDATE",
    });
    const tabs = shallowRef<ITabConfig[]>(SITE_TABS());
    const viewName = "formSite";
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
    });
    const getSites = async () => {
      await getLookups("site/sites?status=ACTIVE&size=100000")
        .then((res: any) => {
          const data = res.content;
          options = [];
          data.map((obj: any) => {
            options.push({
              label: obj.name,
              value: obj.webId,
            });
          });
          const siteIds = allTableData.value.map((item) => Number(item.webId));
          const filteredValues = options.filter(
            (item) => !siteIds.includes(Number(item.value))
          );
          formData.value.model[0].options = filteredValues;
        })
        .catch((err) => {
          return [];
        });
    };
    let formData = ref<IAddEditFormModel>({
      page: "form",
      header: "Form Site",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        siteId: Yup.array()
          .nullable()
          .min(1, t("pages.form.site") + " " + t("common.isRequired"))
          .required(t("pages.form.site") + " " + t("common.isRequired")),
      }),
      label: [{ addHeader: t("pages.form.assignSite") }],
      model: [
        {
          name: "siteId",
          label: t("pages.form.site"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: options,
          multiSelect: true,
        },
      ],
    });
    const edit = (item: any) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      const data = reqData.siteId.map((item) => {
        return {
          siteId: item,
          formDefinitionId: dataProp.value.webId,
        };
      });
      loading.value = true;
      localPreference.value = { ...defaultFilter.value };
      await SiteApi.getInstance()
        .addFormSites(data, formData.value.edit)
        .then(() => {
          toast.success(formSiteAddMsg);
          queryUpdate(dataProp.value.webId, { ...localPreference.value });
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        localPreference.value = { ...defaultFilter.value };
        const data = await SiteApi.getInstance().getFormSitesList(
          dataProp.value.webId,
          {
            ...params,
          }
        );
        const allData = await SiteApi.getInstance().getFormSitesList(
          dataProp.value.webId,
          {
            status: "ACTIVE",
            size: 10000,
          }
        );
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        getSites();
      } finally {
        loading.value = false;
      }
    };

    const onBulkDelete = async (siteIds) => {
      showDeleteDialog.value = false;
      try {
        await FormApi.getInstance()
          .deleteSiteForms(dataProp.value.webId, siteIds)
          .then(() => {
            toast.success(formDeleteMsg);
          })
          .catch((err) => {
            console.error(err);
          });

        refreshList();
      } catch (e) {
        //
      }
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };
    const openDetailModal = async (site) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { data: site } };
      });
      emitter?.emit("VZTabsUpdate", {
        tabs: tabs.value,
        title: site.name,
        icon: "bi bi-diagram-3 fs-2 text-primary",
      });
      if (DrawerComponent?.getInstance("vz_detail_drawer")?.isShown()) {
        DrawerComponent?.getInstance("vz_detail_drawer")?.hide();
      }
      DrawerComponent?.getInstance("vz_detail_drawer")?.toggle();
    };
    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;
    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        allTableData.value = [];
        rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
        lastFetchedWebId = webId;
        queryUpdate(+webId, {
          page: page.value - 1,
          size: rowsPerPage.value,
        });
        if (Datatable.value.VZFilter) {
          Datatable.value.VZFilter.getSavedFilterList();
        }
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, { ...defaultFilter.value });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
          allTableData.value = [];
        }
      }
    );
    return {
      bulkDelete,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      loading,
      selectedForAction,
      syncQuery,
      refreshList,
      formData,
      submit,
      viewName,
      edit,
      onBulkDelete,
      openDetailModal,
      t,
      tabs,
      hasPermission,
      permissionModel,
      showDeleteDialog,
      showDetailDialog,
      Datatable,
      defaultFilter,
      getSites,
    };
  },
});
